import React, { useState, useMemo } from 'react';
import { Button } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CameraOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { capitalize } from 'lodash';
// Helpers
import {
  mapSocialMediaToLabel,
  sortDeliverablesChecklistByOrderIdx,
  sortDeliverablesChecklistByType,
  getPostDueDateLabel,
} from 'helpers/timelineDeliverables';
import { TimeLineDeliverable } from 'helpers/deliverables';
// Types
import {
  CampaignSmPlatform,
  CampaignTaskType,
  CreateCampaignTaskV2Input,
  CreateCampaignContentTaskInput,
} from 'api/graphql-global-types';
import { GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2 } from 'api/campaignV2/types/GetCampaignsV2';
// Components
import TimelineDeliverablesModal from '../TimelineDeliverablesModal/TimelineDeliverablesModal';
import ContentOnlyModal from '../TimelineDeliverablesModal/components/ContentOnlyModal/ContentOnlyModal';
// Types (for content-only UGC)
import { GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks } from 'api/campaignV2/types/GetCampaignsV2';
// Styles
import styles from './TimelineDeliverables.module.scss';

type TimelineDeliverablesProps = {
  deliverablesChecklist: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[];
  setDeliverablesChecklist: (
    updater: (
      prev: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
    ) => GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
  ) => void;
  platform: CampaignSmPlatform;
  error?: string | null;
  /** When true, only content deliverables are allowed (no post tasks). Used for UGC campaigns. */
  contentOnly?: boolean;
};

const TimelineDeliverables: React.FC<TimelineDeliverablesProps> = ({
  deliverablesChecklist,
  setDeliverablesChecklist,
  platform,
  error,
  contentOnly = false,
}: TimelineDeliverablesProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingItem, setEditingItem] =
    useState<GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2 | null>(
      null
    );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const postTaskOptions = useMemo(() => {
    const isTikTok = platform === CampaignSmPlatform.TT;
    return isTikTok
      ? [{ label: 'TikTok Video', value: CampaignTaskType.TT_VIDEO }]
      : [
          { label: 'Live', value: CampaignTaskType.IG_LIVE },
          { label: 'Post', value: CampaignTaskType.IG_POST },
          { label: 'Reel', value: CampaignTaskType.IG_REEL },
          { label: 'Story', value: CampaignTaskType.IG_STORY },
        ];
  }, [platform]);

  const handleSave = (
    data: CreateCampaignTaskV2Input | CreateCampaignContentTaskInput
  ) => {
    if (editingIndex !== null) {
      setDeliverablesChecklist((prev) =>
        sortDeliverablesChecklistByOrderIdx(
          prev.map((item, index) =>
            index === editingIndex ? (data as TimeLineDeliverable) : item
          )
        )
      );
    } else {
      setDeliverablesChecklist((prev) =>
        sortDeliverablesChecklistByType([...prev, data as TimeLineDeliverable])
      );
    }

    setIsOpen(false);
    setEditingIndex(null);
    setEditingItem(null);
  };

  return (
    <div className={styles.root}>
      <h2>Deliverables Checklist</h2>

      <p className={styles.description}>
        Add all of the content creation and social posts required by talent for
        this campaign.
      </p>

      <div className={styles.deliverablesListContainer}>
        {deliverablesChecklist?.map((deliverable, index) => (
          <DeliverableItem
            key={index}
            deliverable={deliverable}
            index={index}
            platform={platform}
            postTaskOptions={postTaskOptions}
            onEdit={(index, deliverable) => {
              setIsOpen(true);
              setEditingIndex(index);
              setEditingItem(deliverable);
            }}
            onRemove={(index) => {
              setDeliverablesChecklist(
                (
                  prev: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
                ) => prev.filter((_, i) => i !== index)
              );
            }}
            isContentOnly={contentOnly}
          />
        ))}
      </div>

      <div className={styles.buttonGroup}>
        <Button
          type="default"
          className={styles.addDeliverableButton}
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          + Add More Deliverables
        </Button>
      </div>
      {error && <p className={styles.error}>{error}</p>}

      {contentOnly ? (
        <ContentOnlyModal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setEditingIndex(null);
            setEditingItem(null);
          }}
          onSubmit={(data: CreateCampaignContentTaskInput) => {
            setEditingIndex(null);
            setEditingItem(null);
            handleSave(data);
          }}
          editingItem={
            editingItem as GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks | null
          }
        />
      ) : (
        <TimelineDeliverablesModal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setEditingIndex(null);
            setEditingItem(null);
          }}
          onSubmit={(data: CreateCampaignTaskV2Input) => {
            setEditingIndex(null);
            setEditingItem(null);
            handleSave(data);
          }}
          platform={platform}
          editingItem={editingItem}
        />
      )}
    </div>
  );
};

// Deliverable Item Component
type DeliverableItemProps = {
  deliverable: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2;
  index: number;
  platform: CampaignSmPlatform;
  postTaskOptions: Array<{ label: string; value: CampaignTaskType }>;
  isContentOnly?: boolean;
  onEdit: (
    index: number,
    deliverable: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2
  ) => void;
  onRemove: (index: number) => void;
};

const DeliverableItem = ({
  deliverable,
  index,
  platform,
  postTaskOptions,
  onEdit,
  onRemove,
  isContentOnly = false,
}: DeliverableItemProps) => {
  return (
    <div className={styles.deliverableItem}>
      <div className={styles.deliverableContent}>
        <div className={styles.contentLeft}>
          <div className={styles.platformLabel}>
            {deliverable.type === CampaignTaskType.CONTENT
              ? 'CONTENT'
              : `${mapSocialMediaToLabel(platform).toUpperCase()} ${
                  postTaskOptions
                    .find((opt) => opt.value === deliverable.type)
                    ?.label?.toUpperCase() ?? 'POST'
                }`}
          </div>

          <h3 className={styles.deliverableTitle}>{deliverable.title}</h3>

          <div className={styles.deliverableInfo}>
            <div className={styles.infoRow}>
              <div className={styles.infoIcon}>
                <CameraOutlined />
              </div>
              <span className={styles.infoLabel}>
                Content {isContentOnly ? 'type' : 'pieces'}:
              </span>
              <span className={styles.infoValue}>
                {isContentOnly
                  ? capitalize((deliverable as any).mediaType)
                  : `${deliverable.contentItems?.length ?? 0} ${
                      deliverable.contentItems?.length === 1
                        ? 'piece'
                        : 'pieces'
                    } of content`}
              </span>
            </div>

            <div className={styles.infoRow}>
              <div className={styles.infoIcon}>
                <CalendarOutlined />
              </div>
              <span className={styles.infoLabel}>
                {isContentOnly ? 'Content' : 'Post'} Due Date:
              </span>
              <span className={styles.infoValue}>
                {getPostDueDateLabel(deliverable)}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.deliverableActions}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.preventDefault();
              onEdit(index, deliverable);
            }}
          >
            Edit
          </Button>

          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.preventDefault();
              onRemove(index);
            }}
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TimelineDeliverables;
